import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCurrencyStore } from '@/stores/currency-store';
import { usePendingBetStore } from '@/stores/pending-bet-store';
import { useCurrentConnection } from '@/hooks/auth';
import useBalance from './use-balance';

// Do not reconcile with chain while bets are unresolved.
const ENABLE_BALANCE_RECONCILIATION = false;

/**
 * Balance lifecycle (logs every change):
 * - baseline starts from chain once.
 * - displayed = baseline - unresolved stakes - unresolved gas + resolved payouts.
 * - when all bets resolve, baseline += (resolved payout - resolved stake - resolved gas), then clear bets.
 */
type UsePendingBetsOptions = {
	enabled?: boolean;
};

export function usePendingBets(options: UsePendingBetsOptions = {}) {
	const { enabled = true } = options;
	const { selectedCurrency } = useCurrencyStore();
	const { account } = useCurrentConnection();

	const pendingBets = usePendingBetStore((s) => s.bets);
	const addPendingBetToStore = usePendingBetStore((s) => s.addPendingBet);
	const resolvePendingBetInStore = usePendingBetStore((s) => s.resolvePendingBet);
	const setPendingBetPayoutInStore = usePendingBetStore((s) => s.setPendingBetPayout);
	const setPendingBetGasInStore = usePendingBetStore((s) => s.setPendingBetGas);
	const clearPendingBetsForCurrency = usePendingBetStore((s) => s.clearByCurrency);

	const currencyBets = useMemo(() => pendingBets.filter((b) => b.currencyType === selectedCurrency.type), [pendingBets, selectedCurrency.type]);
	const unresolved = useMemo(() => currencyBets.filter((b) => !b.resolved), [currencyBets]);
	const resolved = useMemo(() => currencyBets.filter((b) => b.resolved), [currencyBets]);
	const shouldPollChainBalance = unresolved.length === 0;

	const { balance: chainBalance } = useBalance(account?.address, selectedCurrency.type, {
		refetchInterval: shouldPollChainBalance ? 5000 : false,
		enabled,
	});

	const [baseline, setBaseline] = useState<number | null>(chainBalance ?? null);
	const pendingBaselineRef = useRef<number | null>(null);

	// Initialize baseline once from chain
	useEffect(() => {
		if (!enabled) return;
		if (baseline == null && chainBalance != null) {
			setBaseline(chainBalance);
		}
	}, [baseline, chainBalance, enabled]);

	// When idle (no unresolved bets), keep baseline synced to on-chain balance updates.
	useEffect(() => {
		if (!enabled) return;
		if (unresolved.length > 0) return;
		if (chainBalance == null) return;
		if (pendingBaselineRef.current != null) {
			if (chainBalance !== pendingBaselineRef.current) {
				return;
			}
			pendingBaselineRef.current = null;
		}
		if (baseline == null || baseline !== chainBalance) {
			setBaseline(chainBalance);
		}
	}, [baseline, chainBalance, enabled, unresolved.length]);

	const unresolvedStake = useMemo(() => unresolved.reduce((sum, b) => sum + b.stake, 0), [unresolved]);
	const unresolvedGas = useMemo(() => unresolved.reduce((sum, b) => sum + (b.gasUsed ?? 0), 0), [unresolved]);
	const resolvedPayout = useMemo(() => resolved.reduce((sum, b) => sum + (b.payout ?? 0), 0), [resolved]);
	const resolvedStake = useMemo(() => resolved.reduce((sum, b) => sum + b.stake, 0), [resolved]);
	const resolvedGas = useMemo(() => resolved.reduce((sum, b) => sum + (b.gasUsed ?? 0), 0), [resolved]);

	const displayedBalance = useMemo(() => {
		if (baseline == null) return null;
		// Subtract resolved stakes/gas so UI doesn't briefly jump before baseline is reconciled.
		return Math.max(0, baseline - unresolvedStake - unresolvedGas - resolvedStake - resolvedGas + resolvedPayout);
	}, [baseline, unresolvedGas, unresolvedStake, resolvedGas, resolvedPayout, resolvedStake]);

	const previousDisplayRef = useRef<number | null>(null);

	useEffect(() => {
		const current = displayedBalance ?? null;
		const previous = previousDisplayRef.current;
		if (current === previous) return;
		previousDisplayRef.current = current;
	}, [baseline, displayedBalance, unresolvedGas, unresolvedStake, resolvedGas, resolvedPayout, resolvedStake]);

	// Apply net when all bets for this currency are resolved
	useEffect(() => {
		if (!enabled) return;
		if (currencyBets.length === 0) return;
		if (resolved.length === 0) return;
		if (resolved.length !== currencyBets.length) return;
		if (baseline == null) return;

		const net = resolvedPayout - resolvedStake - resolvedGas;
		const next = Math.max(0, baseline + net);
		pendingBaselineRef.current = next;
		setBaseline(next);
		clearPendingBetsForCurrency(selectedCurrency.type);
	}, [
		baseline,
		clearPendingBetsForCurrency,
		currencyBets.length,
		enabled,
		resolved.length,
		resolvedGas,
		resolvedPayout,
		resolvedStake,
		selectedCurrency.type,
	]);

	// Optional reconciliation when idle (disabled by default)
	useEffect(() => {
		if (!enabled) return;
		if (!ENABLE_BALANCE_RECONCILIATION) return;
		if (currencyBets.length > 0) return;
		if (chainBalance == null) return;
		setBaseline(chainBalance);
	}, [chainBalance, currencyBets.length, enabled]);

	const addPendingBet = useCallback(
		(stake: number, id?: string, currencyType?: string) => {
			return addPendingBetToStore(stake, currencyType || selectedCurrency.type, id);
		},
		[addPendingBetToStore, selectedCurrency.type],
	);

	const setPendingBetPayout = useCallback(
		(betId: string, payout: number) => {
			const safe = Math.max(0, payout);
			setPendingBetPayoutInStore(betId, safe);
		},
		[setPendingBetPayoutInStore],
	);

	const setPendingBetGas = useCallback(
		(betId: string, gasUsed: number) => {
			const safe = Math.max(0, gasUsed);
			setPendingBetGasInStore(betId, safe);
		},
		[setPendingBetGasInStore],
	);

	const resolvePendingBet = useCallback(
		(betId: string) => {
			resolvePendingBetInStore(betId);
		},
		[resolvePendingBetInStore],
	);

	const resetPendingBetsState = useCallback(() => {
		pendingBaselineRef.current = null;
		setBaseline(chainBalance ?? null);
	}, [chainBalance]);

	return {
		balance: chainBalance,
		displayedBalance,
		availableBalance: displayedBalance,
		addPendingBet,
		setPendingBetPayout,
		setPendingBetGas,
		resolvePendingBet,
		resetPendingBetsState,
	};
}
